/** Strip date-mode-irrelevant fields and prepare venueIds for the API. */
export function transformMiqaatPayload(data: Record<string, unknown>) {
  const d = { ...data };
  if (d.dateMode === "TEMPORARY") {
    d.hijriMonth = null;
    d.hijriDay = null;
  } else if (d.dateMode === "PERMANENT") {
    d.englishDate = null;
  }

  if (typeof d.englishDate === "string" && d.englishDate.trim()) {
    // DateInput is YYYY-MM-DD; tolerate accidental ISO datetimes by keeping the calendar prefix.
    const trimmed = d.englishDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      d.englishDate = trimmed.slice(0, 10);
    }
  }

  if (Array.isArray(d.venueIds)) {
    // API denormalizes `venue` from linked venue names when venueIds is sent.
    delete d.venue;
    d.venueIds = d.venueIds.filter(
      (id): id is string => typeof id === "string" && id.trim().length > 0
    );
    const ids = d.venueIds as string[];
    if (
      typeof d.defaultVenueId === "string" &&
      d.defaultVenueId &&
      !ids.includes(d.defaultVenueId)
    ) {
      delete d.defaultVenueId;
    }
    if (ids.length < 2) {
      delete d.defaultVenueId;
    }
  }

  if (d.defaultVenueId === "" || d.defaultVenueId === null || d.defaultVenueId === undefined) {
    delete d.defaultVenueId;
  }

  // Family check-in defaults off; only meaningful when WhatsApp self-scan is on.
  d.familySelfCheckInEnabled = Boolean(d.selfCheckInEnabled) && Boolean(d.familySelfCheckInEnabled);

  return d;
}

/** Validates RSVP cutoff hours (0–8760). */
export function validateMiqaatRsvpCutoff(values: Record<string, unknown>) {
  const closes = values.rsvpClosesHoursBeforeEvent;
  if (closes === undefined || closes === null || closes === "") {
    return {};
  }
  const c = Number(closes);
  if (!Number.isFinite(c)) {
    return {};
  }
  if (!Number.isInteger(c) || c < 0 || c > 8760) {
    return {
      rsvpClosesHoursBeforeEvent: "Cutoff must be an integer from 0 to 8760",
    };
  }
  return {};
}

/** Validates self-scan check-in opening cutoff hours (0–8760). */
export function validateMiqaatSelfCheckInOpensHours(values: Record<string, unknown>) {
  const opens = values.selfCheckInOpensHoursBeforeEvent;
  if (opens === undefined || opens === null || opens === "") {
    return {};
  }
  const o = Number(opens);
  if (!Number.isFinite(o)) {
    return {};
  }
  if (!Number.isInteger(o) || o < 0 || o > 8760) {
    return {
      selfCheckInOpensHoursBeforeEvent: "Cutoff must be an integer from 0 to 8760",
    };
  }
  return {};
}

/** Admin UX: at least one linked venue (API still allows empty). */
export function validateMiqaatVenues(values: Record<string, unknown>) {
  const raw = values.venueIds;
  const ids = Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === "string" && id.trim().length > 0)
    : [];
  if (ids.length === 0) {
    return { venueIds: "Select at least one venue" };
  }
  return {};
}

/** Combined SimpleForm validate for create/edit. */
export function validateMiqaatForm(values: Record<string, unknown>) {
  return {
    ...validateMiqaatRsvpCutoff(values),
    ...validateMiqaatSelfCheckInOpensHours(values),
    ...validateMiqaatVenues(values),
  };
}
